import styles from './index.module.scss'
import type { ElementType, FunctionComponent } from 'react'
import React from 'react'
import { Icon } from '../Icon'
import { TooltipPopOver } from '../TooltipPopOver'

export type DragHandleContentProps = {
  disabled?: boolean
  title?: string
  icon?: string
  className?: string
  dragHandleName?: string
}

// The `& Record<string, unknown>` widening lets `DragHandle` forward
// arbitrary drag-source props (`draggable`, `onDragStart`, `onDragEnd`,
// `data-drag-sort-index`) to the rendered host element when consumers
// spread `{...getHandleProps(index)}` on it. A tighter type (e.g.,
// `Partial<DragSortHandleProps>`) collides with React's `ElementType`
// generic: when `As` is a string like `'td'`, TS infers the JSX host type
// as the full HTML+SVG element union, and `DragEvent<HTMLElement>`
// handler params are not assignable to `DragEvent<SVGSymbolElement>`.
// The `Record` widening is the least-bad option for this specific
// type-inference boundary.
export type DragHandleProps = Omit<DragHandleContentProps, 'className'> & {
  As?: FunctionComponent<DragHandleContentProps> | ElementType
} & Record<string, unknown>

export const DragHandle = ({
  disabled,
  title,
  // Default class is `'drag-sort-handle'` (NOT the generic `'drag-handle'`) to avoid colliding with
  // `@ztechapps/webui.modal`'s `<Draggable handle=".drag-handle">` selector. When a drag-sortable
  // table is rendered inside a `webui.modal`, the modal's `react-draggable` would otherwise see the
  // row's drag-handle as a grab-source for moving the dialog — both drag systems fire on the same
  // mousedown. Renaming the default at the source eliminates the collision for every consumer; the
  // modal's own `<ModalDragHandle>` continues to set `className="drag-handle"` directly so dialog
  // dragging is unaffected. See `webui-drag-and-drop-style-guide.md` § 10.
  dragHandleName = 'drag-sort-handle',
  As = 'div',
  ...rest
}: DragHandleProps) => {
  const dragHandleWithDisabledClassName = disabled ? 'drag-handle-disabled' : `${dragHandleName} ${styles.dragHandle}`
  const resolvedTitle = title || (disabled ? 'Dragging is disabled' : 'Drag to sort.')

  const icon = 'grip-vertical'

  //when there is a value of As component use it passing DragHandleContentProps
  //when the type of As is string, this is the element name
  if (typeof As === 'string') {
    return (
      <TooltipPopOver tooltipBody={resolvedTitle}>
        <As
          {...rest}
          className={`px-0 ${dragHandleWithDisabledClassName} text-center text-primary`}
          disabled={disabled}
        >
          <Icon name={icon} className={`${disabled ? 'text-body-secondary' : ''}`} />
        </As>
      </TooltipPopOver>
    )
  } else {
    //this is a component so we pass the component the DragHandleContentProps
    // plus any additional props (e.g., draggable / data-* / drag handlers) that
    // the component will forward to its root element.
    return <As {...rest} {...{ disabled, title: resolvedTitle, icon, className: dragHandleWithDisabledClassName }} />
  }
}
